import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const KEY = 'onda_cookie_consent';

/**
 * Onda cookie catcher — GDPR/Aus-Privacy style consent banner.
 * Shows once until the visitor chooses. Non-essential cookies (analytics /
 * marketing pixels) must wait for "accepted": listen for the
 * `onda-cookie-consent` window event, or read localStorage[KEY], before loading them.
 */
export function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setShow(true);
    } catch (_) {
      /* storage blocked (e.g. strict privacy mode) — don't nag */
    }
  }, []);

  const choose = (value) => {
    try {
      localStorage.setItem(KEY, value);
    } catch (_) {}
    // fire a consent event so any tracking code can gate on it
    window.dispatchEvent(new CustomEvent('onda-cookie-consent', { detail: value }));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-3 bottom-3 z-[9999] mx-auto max-w-2xl rounded-2xl border border-[#D4956A]/30 bg-[#1A1A2E]/95 p-5 shadow-2xl backdrop-blur"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#D4956A]">Cookies</p>
      <p className="mt-2 text-sm leading-relaxed text-neutral-200">
        Onda uses cookies to keep you signed in, remember your preferences, and understand how the
        app is used so we can make it better. Accept to help us improve, or decline and we'll use
        only what's essential to run the service.{' '}
        <Link to="/privacy" className="text-[#D4956A] underline underline-offset-2 hover:text-[#e6b085]">
          Cookie &amp; Privacy Policy
        </Link>
        .
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => choose('accepted')}
          className="flex-1 rounded-lg bg-[#D4956A] px-4 py-2.5 text-sm font-semibold text-[#1A1A2E] transition hover:bg-[#e6b085]"
        >
          Accept
        </button>
        <button
          type="button"
          onClick={() => choose('declined')}
          className="flex-1 rounded-lg border border-neutral-600 px-4 py-2.5 text-sm font-semibold text-neutral-200 transition hover:border-neutral-400"
        >
          Decline — essential only
        </button>
      </div>
    </div>
  );
}

export default CookieConsent;
